import { EventEffects } from '../effects/EventEffects'
import { FundsForceField } from '../physics/FundsForceField'
import { PHYSICS_RADIUS_SCALE } from '../physics/DynamicCollisionForce'
import { FundsCanvasRenderer } from '../renderer/FundsCanvasRenderer'
import type {
  FlowNode,
  MarketFlowData,
  MarketFrame,
  TimelineSample,
  VisualizationConfig,
} from '../types/market'
import { hashString, lerp, monotoneCubicInterpolate, radiusForFlow } from '../utils/math'

export interface VisualizationPerformanceMeasurement {
  physicsMs: number
  renderMs: number
  bubbleCount: number
  simulationAlpha: number
}

interface VisualizationRenderOptions {
  freezePhysics?: boolean
  effectsTimelineMs?: number
  allowChampionParticleEmission?: boolean
}

export class FundsFlowVisualization {
  private readonly nodes: FlowNode[]
  private readonly frameMaps: Array<Map<string, MarketFrame['sectors'][number]>>
  private readonly effects: EventEffects
  private readonly forceField: FundsForceField
  private readonly renderer: FundsCanvasRenderer
  private readonly performanceMeasurement: VisualizationPerformanceMeasurement = {
    physicsMs: 0,
    renderMs: 0,
    bubbleCount: 0,
    simulationAlpha: 0,
  }
  private readonly rankOrder: FlowNode[] = []
  private lastLeftIndex = -1
  private lastTimelineMs = -1

  constructor(
    canvas: HTMLCanvasElement,
    private readonly data: MarketFlowData,
    private readonly config: VisualizationConfig,
    private readonly renderTitle = '全市场资金流向',
    isExportMode = false,
    private readonly performanceEnabled = false,
  ) {
    const first = data.frames[0]
    this.nodes = first.sectors.map((sector, index) => {
      const seed = hashString(sector.id)
      return {
        id: sector.id,
        name: sector.displayName ?? sector.name,
        flow: sector.flow,
        previousFlow: sector.flow,
        targetFlow: sector.flow,
        radius: radiusForFlow(sector.flow, config.minRadius, config.maxRadius),
        targetRadius: radiusForFlow(sector.flow, config.minRadius, config.maxRadius),
        polarity: Math.sign(sector.flow),
        currentSign: sector.flow < 0 ? -1 : 1,
        targetSign: sector.flow < 0 ? -1 : 1,
        physicsState: sector.flow < 0 ? 'STABLE_NEGATIVE' : 'STABLE_POSITIVE',
        isCrossing: false,
        crossingArrived: false,
        crossingProgress: 1,
        crossingStartedAt: -Infinity,
        crossingStartY: 0,
        crossingLaneOffset: 0,
        collisionScale: 1,
        collisionRadius: radiusForFlow(sector.flow, config.minRadius, config.maxRadius) * PHYSICS_RADIUS_SCALE + config.collisionPadding,
        targetY: 0,
        rank: index,
        depth: 0.75,
        seed,
        x: config.safeArea.left + 86 + ((seed >>> 0) % 830),
        y: sector.flow >= 0 ? 450 + ((seed >>> 0) % 340) : 1040 + ((seed >>> 0) % 350),
        vx: 0,
        vy: 0,
      }
    })
    this.frameMaps = data.frames.map((frame) => new Map(frame.sectors.map((sector) => [sector.id, sector])))
    this.effects = new EventEffects(config)
    this.forceField = new FundsForceField(this.nodes, config)
    this.renderer = new FundsCanvasRenderer(canvas, config, isExportMode)
    this.updateNodes(0, 0, 0)
    this.forceField.stabilize()
  }

  render(sample: TimelineSample, options: VisualizationRenderOptions = {}): VisualizationPerformanceMeasurement {
    const frameStartedAt = this.performanceEnabled ? performance.now() : 0
    const effectsTimelineMs = options.effectsTimelineMs ?? sample.elapsedMs
    const hasRenderedFrame = this.lastLeftIndex >= 0
    const discontinuity =
      sample.elapsedMs < this.lastTimelineMs ||
      (this.lastTimelineMs >= 0 && sample.elapsedMs - this.lastTimelineMs > this.config.frameDuration * 7)
    if (discontinuity) this.resetAt(sample)

    if (!options.freezePhysics) this.updateNodes(sample.leftIndex, sample.rightIndex, sample.progress)
    const leftFrame = this.data.frames[sample.leftIndex]
    const rightFrame = this.data.frames[sample.rightIndex]
    this.effects.prepareTransition(
      leftFrame,
      rightFrame,
      this.frameMaps[sample.rightIndex],
      sample.leftIndex,
      effectsTimelineMs,
    )
    const minuteChanged = sample.leftIndex !== this.lastLeftIndex
    const physicsStartedAt = this.performanceEnabled ? performance.now() : 0
    if (!options.freezePhysics) {
      this.forceField.updateForces(minuteChanged, sample.elapsedMs)
      this.forceField.tick(1, sample.elapsedMs)
      this.updateRanksAndDepth()
    }
    this.effects.recordPositions(this.nodes, effectsTimelineMs, {
      allowChampionParticleEmission: options.allowChampionParticleEmission,
    })
    const renderStartedAt = this.performanceEnabled ? performance.now() : 0
    if (this.performanceEnabled) this.performanceMeasurement.physicsMs = renderStartedAt - physicsStartedAt
    this.renderer.render({
      date: this.data.date,
      title: this.renderTitle,
      leftFrame,
      rightFrame,
      nodes: this.nodes,
      progress: sample.progress,
      indexPrice: this.interpolateIndex(sample.leftIndex, sample.rightIndex, sample.progress, 'price'),
      indexChangePercent: this.interpolateIndex(sample.leftIndex, sample.rightIndex, sample.progress, 'changePercent'),
      timelineMs: effectsTimelineMs,
      frameIndex: sample.leftIndex,
      zeroAxisY: this.forceField.zeroAxisY,
      effects: this.effects,
    })
    if (this.performanceEnabled) this.performanceMeasurement.renderMs = performance.now() - renderStartedAt
    if (this.performanceEnabled && hasRenderedFrame && minuteChanged) {
      performance.measure('market-data-switch', { start: frameStartedAt, end: performance.now() })
    }
    this.performanceMeasurement.bubbleCount = this.nodes.length
    this.performanceMeasurement.simulationAlpha = this.forceField.alpha
    this.lastLeftIndex = sample.leftIndex
    this.lastTimelineMs = sample.elapsedMs
    return this.performanceMeasurement
  }

  renderExportFrame(sample: TimelineSample, frameNumber: number, options: VisualizationRenderOptions = {}): void {
    const repeatedTimelineFrame = sample.elapsedMs === this.lastTimelineMs
    if (frameNumber === 0 || sample.elapsedMs < this.lastTimelineMs || (repeatedTimelineFrame && !options.freezePhysics)) {
      this.resetAt(sample)
    }
    this.render(sample, options)
  }

  destroy(): void {
    this.forceField.destroy()
  }

  getPhysicsSnapshot(): Array<{
    id: string
    flow: number
    x: number
    y: number
    targetY: number
    targetSign: 1 | -1
    state: FlowNode['physicsState']
    isCrossing: boolean
    crossingArrived: boolean
    crossingProgress: number
    collisionScale: number
    collisionRadius: number
    radius: number
    polarity: number
  }> {
    return this.nodes.map((node) => ({
      id: node.id,
      flow: node.flow,
      x: node.x ?? 0,
      y: node.y ?? 0,
      targetY: node.targetY,
      targetSign: node.targetSign,
      state: node.physicsState,
      isCrossing: node.isCrossing,
      crossingArrived: node.crossingArrived,
      crossingProgress: node.crossingProgress,
      collisionScale: node.collisionScale,
      collisionRadius: node.collisionRadius,
      radius: node.radius,
      polarity: node.polarity,
    }))
  }

  private updateNodes(leftIndex: number, rightIndex: number, progress: number): void {
    const left = this.frameMaps[leftIndex]
    const right = this.frameMaps[rightIndex]
    const previous = this.frameMaps[Math.max(0, leftIndex - 1)]
    const following = this.frameMaps[Math.min(this.frameMaps.length - 1, rightIndex + 1)]
    for (const node of this.nodes) {
      const currentSector = left.get(node.id)
      const nextSector = right.get(node.id)
      if (!currentSector || !nextSector) continue
      const previousFlow = previous.get(node.id)?.flow ?? currentSector.flow
      const followingFlow = following.get(node.id)?.flow ?? nextSector.flow
      node.previousFlow = currentSector.flow
      node.targetFlow = nextSector.flow
      node.targetSign = nextSector.flow < 0 ? -1 : nextSector.flow > 0 ? 1 : node.targetSign
      node.flow = monotoneCubicInterpolate(
        previousFlow,
        currentSector.flow,
        nextSector.flow,
        followingFlow,
        progress,
      )
      node.targetRadius = radiusForFlow(node.flow, this.config.minRadius, this.config.maxRadius)
      node.radius = lerp(node.radius, node.targetRadius, 0.24)
      if (!node.isCrossing) node.polarity = node.currentSign
    }
  }

  private interpolateIndex(
    leftIndex: number,
    rightIndex: number,
    progress: number,
    key: 'price' | 'changePercent',
  ): number {
    const currentFrame = this.data.frames[leftIndex]
    const nextFrame = this.data.frames[rightIndex]
    if (currentFrame.index.available === false) return currentFrame.index[key]
    const previousFrame = this.data.frames[Math.max(0, leftIndex - 1)]
    const followingFrame = this.data.frames[Math.min(this.data.frames.length - 1, rightIndex + 1)]
    const previous = previousFrame.index.available === false ? currentFrame.index[key] : previousFrame.index[key]
    const current = currentFrame.index[key]
    const next = nextFrame.index.available === false ? current : nextFrame.index[key]
    const following = followingFrame.index.available === false ? next : followingFrame.index[key]
    return monotoneCubicInterpolate(previous, current, next, following, progress)
  }

  private updateRanksAndDepth(): void {
    this.rankOrder.length = this.nodes.length
    for (let index = 0; index < this.nodes.length; index += 1) this.rankOrder[index] = this.nodes[index]
    this.rankOrder.sort((a, b) => Math.abs(b.flow) - Math.abs(a.flow))
    this.rankOrder.forEach((node, index) => {
      node.rank = lerp(node.rank, index, 0.18)
      const targetDepth = index === 0 ? 1 : index < 3 ? 0.92 : index < 10 ? 0.84 : 0.74
      node.depth = lerp(node.depth, targetDepth, 0.1)
    })
  }

  private resetAt(sample: TimelineSample): void {
    this.effects.reset()
    this.renderer.reset()
    this.lastLeftIndex = -1
    this.lastTimelineMs = -1
    this.updateNodes(sample.leftIndex, sample.rightIndex, sample.progress)
    for (const node of this.nodes) node.radius = node.targetRadius
    this.forceField.reset()
  }
}
